import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import _ from 'lodash';

import {
  selectEntityReferenceCandidatesByType,
  selectEntityReferenceLoadedApplicationId,
} from 'store/loanApplications/selectors';

import { getSoftLinkifyMatchAtCaret } from '../../../TeamChat.entityReferences.utils';
import { getApplicationIdFromChannelId } from '../../../TeamChat.utils';

// Soft-linkify works on both of a loan's channels (internal + TPO, DEV-5697). It reads the SAME
// candidate slice the `#` picker ensures (loaded via useChatEntityReferences in the same composer),
// so it does NOT trigger its own fetch — it only offers once that slice holds THIS chat's loan.
const useChatSoftLinkify = ({ chatId }) => {
  const applicationId = getApplicationIdFromChannelId(chatId);
  const candidatesByType = useSelector(selectEntityReferenceCandidatesByType);
  const loadedApplicationId = useSelector(selectEntityReferenceLoadedApplicationId);

  const isLoadedForApplication =
    !!applicationId && loadedApplicationId?.toLowerCase() === applicationId.toLowerCase();

  const [match, setMatch] = useState(null);
  const dismissedStartRef = useRef(null);

  const updateFromCaret = useCallback(
    (value, caret, { isSuppressed = false } = {}) => {
      if (isSuppressed || !isLoadedForApplication) {
        setMatch(null);

        return;
      }

      const nextMatch = getSoftLinkifyMatchAtCaret({ text: value, caret, candidatesByType });

      // Mirrors the `#` picker's dismiss latch: once Esc dismisses a match, the same span must not
      // re-offer until the caret leaves it (a new tail clears the latch); otherwise the next keyup
      // instantly re-detects the identical label and re-opens the hint.
      if (!nextMatch) {
        dismissedStartRef.current = null;
        setMatch(null);

        return;
      }

      if (dismissedStartRef.current === nextMatch.start) {
        setMatch(null);

        return;
      }

      setMatch(nextMatch);
    },
    [isLoadedForApplication, candidatesByType],
  );

  const dismiss = useCallback(() => {
    setMatch(previous => {
      dismissedStartRef.current = previous?.start ?? null;

      return null;
    });
  }, []);

  const reset = useCallback(() => {
    dismissedStartRef.current = null;
    setMatch(null);
  }, []);

  return {
    match: isLoadedForApplication && !_.isNil(match) ? match : null,
    updateFromCaret,
    dismiss,
    reset,
  };
};

export default useChatSoftLinkify;
